import React from 'react'
import { useTranslation } from 'react-i18next'
import { SmartIdInitiateAuthDto } from 'auth/interfaces/i-smart-id'
import { getReturnUrl } from 'auth/utils/authHelpers'
import appConfig from 'app.config'
import authenticationService from 'auth/services/authenticationService'
import { FormControl, Form } from 'react-bootstrap'
import { CircleSpinner } from 'shared/components/Spinners'
import { formatSsn, validateSsn } from 'original/utils/validation.helper'
import { logError } from 'shared/AppInsight'
import './smartIdContentBlock.scss'
import { ErrorMessage } from 'shared'
import TermsAndConditionBlock from 'auth/constants/termsAndConditions/termsAndConditionsBlock'

function SmartIdAdvLoginBlock() {
	const [isLoading, setIsLoading] = React.useState(false)
	const [error, setError] = React.useState<Error>()
	const [smartIdData, setSmartIdData] =
		React.useState<SmartIdInitiateAuthDto>()
	const [nationalIdentityNumber, setNationalIdentityNumber] =
		React.useState('')
	const [nationalIdentityNumberIsValid, setNationalIdentityNumberIsValid] =
		React.useState(true)

	const { t } = useTranslation()

	async function doSmartIdAuth() {
		const isValid = validateNationalIdentityNumber()

		if (!isValid) {
			return
		}

		setIsLoading(true)

		try {
			const countryCode = appConfig.country
			const data = await authenticationService.smartIdAuthInit({
				countryCode,
				nationalIdentityNumber,
				returnUrl: getReturnUrl(),
			})

			setSmartIdData(data)
			setIsLoading(false)

			const { redirectUrl, secret } =
				await authenticationService.smartIdAuthComplete(data)

			authenticationService.redirectTo(
				redirectUrl || getReturnUrl(),
				secret,
			)
		} catch (error: any) {
			setSmartIdData(undefined)
			logError(error)
			setError(new Error(t('AuthenticationError')))
		} finally {
			setIsLoading(false)
		}
	}

	function onNationalIdentityNumberChange(e: React.FormEvent<any>) {
		const value = formatSsn(
			e.currentTarget.value as string,
			nationalIdentityNumber,
		)
		setNationalIdentityNumber(value || '')
	}

	function validateNationalIdentityNumber() {
		const isValid = validateSsn(nationalIdentityNumber)
		setNationalIdentityNumberIsValid(isValid)
		return isValid
	}

	if (isLoading) {
		return (
			<div className="smart-id-container">
				<CircleSpinner />
			</div>
		)
	}

	if (smartIdData) {
		return (
			<div className="smart-id-container">
				<div>
					<div className="verification-box">
						<p>{t('VerificationCode')}</p>
						<span>{smartIdData.code}</span>
						<CircleSpinner className="my-3" />
					</div>
					<div className="info-box d-flex">
						<div className="mr-1 tooltip-tip" />
						<div className="">{t('SmartIdPin1Message')}.</div>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="smart-id-container">
			<div className="login-description">
				{t('SmartIdDocumentNumber')}
			</div>
			<div className="d-flex flex-column align-items-center">
				<FormControl
					type="tel"
					name="personalNumber"
					id="personalNumber"
					className="mt-2"
					placeholder={t('SSN')}
					onChange={onNationalIdentityNumberChange}
					onBlur={validateNationalIdentityNumber}
					isInvalid={!nationalIdentityNumberIsValid}
					value={nationalIdentityNumber}
				/>
				<Form.Control.Feedback type="invalid">
					{t('InvalidSsn')}
				</Form.Control.Feedback>
			</div>
			{error && <ErrorMessage message={error.message} css="mt-2" />}
			<button onClick={doSmartIdAuth} className="btn btn-primary mt-4">
				{t('Proceed')}
			</button>
			<TermsAndConditionBlock className="mt-4" />
		</div>
	)
}

export default SmartIdAdvLoginBlock
